export const OTP_LENGTH = 6;

/**
 * The card's heading and supporting line per sign-in step, verbatim from the
 * sign-in artboard of login_org_setup.html:
 *
 *   const headings  = { password: 'Sign in to continue', otpEmail: 'Log in with OTP',
 *                       otpVerify: 'Verify OTP' };
 *   const subtitles = { password: '',
 *                       otpEmail:  'Enter your registered email and we will send you a one-time password.',
 *                       otpVerify: `Enter the 6-digit code sent to ${s.email || 'your email'}.` };
 *
 * The verify subtitle names the address, so it is built rather than stored.
 */
export const LOGIN_HEADINGS = {
  password: 'Sign in to continue',
  'otp-send': 'Log in with OTP',
  'otp-verify': 'Verify OTP',
} as const;

export const OTP_SEND_SUBTITLE =
  'Enter your registered email and we will send you a one-time password.';

/** The supplier portal's sign-in screens (2026-10-23): the OTP screens, worded for a supplier. */
export const SUPPLIER_LOGIN_HEADINGS = {
  'otp-send': 'Supplier Log In',
  'otp-verify': 'Verify OTP',
} as const;

export const SUPPLIER_OTP_SEND_SUBTITLE =
  'Enter the email address your organization registered for you as a supplier, and we will send you a one-time password.';

export function otpVerifySubtitle(email: string): string {
  return `Enter the ${String(OTP_LENGTH)}-digit code sent to ${email || 'your email'}.`;
}

/*
  Both windows are now published by `POST /auth/otp/request`, which answers
  `{ expiresIn, resendAfter }` taken from `platform.identity.otp-ttl` and
  `platform.identity.otp-resend-interval`. The screen counts down from the
  server's numbers.

  These two constants remain as the mock-mode values and as the fallback for a
  server that does not report the window -- they are no longer a contract kept
  in step by hand, which is what let them drift with nothing able to notice.
*/

/**
 * The reference's own timer: `state = { ..., seconds: 30 }` in the sign-in
 * artboard of login_org_setup.html, counting down as "Resend OTP in mm:ss".
 * Matches the server's default `otp-resend-interval`.
 */
export const OTP_RESEND_COOLDOWN_SECONDS = 30;

/** Matches the server's default `otp-ttl` (5m). */
export const OTP_TTL_SECONDS = 300;

export const OTP_MAX_ATTEMPTS = 5;

/** Simulated latency, so loading and disabled states are actually visible. */
export const MOCK_LATENCY_MS = 400;

/** Mock-mode credentials. Real auth ignores these entirely. */
export const DEMO_PASSWORD = 'password123';
export const DEMO_OTP = '123456';

/** Copy from the error-handling matrix, mdfiles/loginapi.md section 8. */
export const AUTH_MESSAGES = {
  INVALID_CREDENTIALS: 'Invalid username or password',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  UNEXPECTED_RESPONSE: 'Server error, please try again',
  PROFILE_UNAVAILABLE: 'Signed in, but your profile could not be loaded.',
  OTP_SENT: 'OTP sent successfully',
  /* The supplier portal (2026-10-23): mock mode has no supplier accounts to sign in. */
  SUPPLIER_MOCK_UNAVAILABLE: 'Supplier sign-in is not available while mock data is enabled.',
  /* NB-547: the platform's 400 for POST /auth/otp/request with an address no user holds, on the `email` field. */
  EMAIL_NOT_REGISTERED: 'This email address is not registered. Please enter a registered email address.',
  /* The three one-time-code refusals, in the platform's own words (POST /auth/otp/verify). */
  INVALID_OTP: 'Invalid OTP. Please enter a valid OTP.',
  OTP_EXPIRED: 'OTP has expired. Please request a new OTP.',
  TOO_MANY_ATTEMPTS: 'Too many invalid attempts. Please request a new OTP.',
  /* Change Password: the platform's 400 for a wrong Old Password, on the `currentPassword` field. */
  OLD_PASSWORD_INCORRECT: 'The old password is incorrect.',
  OTP_NOT_SENT: 'Please request an OTP first.',
  RESEND_FAILED: 'Failed to resend OTP. Please try again.',
} as const;

/** Refresh this many ms before the stored expiry rather than exactly on it. */
export const TOKEN_REFRESH_SKEW_MS = 5000;
